import { z } from "zod";
import { isoDate } from "../schemas/common";

/**
 * What an installation sends to core (ADR 0016; docs/admin-app/figures.md › Runtime figures, The
 * report): a cheap **status** every 60 seconds and the **installation report** every hour.
 *
 * The contract only grows. Every section is optional, so an installation can start with a few;
 * every object keeps the fields it doesn't know, so a newer installation's figures survive an
 * older core; names are short strings rather than enums for the same reason (`vocabulary.ts`
 * lists them). Adding a field means: optional here, a fixture that has it, a test without it.
 */

/** Rises only when the meaning of a field changes, never for an added one. */
export const REPORT_VERSION = 1;

/** Days an installation keeps its day counts and demo outcomes, which a full resend carries. */
export const KEPT_DAYS = 400;
/** Days a report carries unless core asks for all `KEPT_DAYS`. */
export const REPORTED_DAYS = 35;
/** Room for every kept day, today and a clock that is a little off. */
const MAX_DAYS = KEPT_DAYS + 50;

const count = z.int().nonnegative();
const amount = z.number().nonnegative();
const name = z.string().min(1).max(200);
const value = z.string().max(200);
const instant = z.iso.datetime();
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

/** An object that keeps the fields it doesn't know. */
const record = <T extends z.ZodRawShape>(shape: T) => z.looseObject(shape);
/** A section of figures: every field optional, unknown ones kept. */
const section = <T extends z.ZodRawShape>(shape: T) => z.looseObject(shape).partial();
const list = <T extends z.ZodType>(item: T, max: number) => z.array(item).max(max);

/** Counts per value, e.g. vehicles per make; the 3-garage rule is applied before they are sent. */
const tally = (max = 500) => list(record({ value, count }), max);

/** Counts per UTC day. */
const perDay = list(record({ day: isoDate, count }), MAX_DAYS);

/** A distribution as `distribution()` returns it (`measures.ts`). */
export const distributionSchema = record({
  n: count,
  none: count,
  p25: amount,
  median: amount,
  p75: amount,
  p90: amount,
  max: amount,
  mean: amount,
  /** Counts per bucket of `HISTOGRAM_EDGES`. */
  histogram: list(count, 64),
});

/** Garages or users active in the last 1, 7 and 30 UTC days, today included. */
const active = section({ last1: count, last7: count, last30: count });

/** When people or garages were last seen: per `RECENCY_DAYS` bucket and over a year, or never. */
const recency = section({
  buckets: list(count, 16),
  never: count,
  /** Counted by their last sign-in or newest record, since they haven't been seen since tracking began. */
  beforeTracking: count,
});

/** The three figures every kind of record gets: the total, per garage, and added per day. */
const RECORD_FIGURES = {
  total: count,
  /** Over all garages, and over those that have at least one. */
  perGarage: section({ all: distributionSchema, using: distributionSchema }),
  /** By the time in the UUIDv7 id, so an imported package counts on the day it was imported. */
  addedPerDay: perDay,
};

/** A garage in a top list: its id, and its name except in the public demo. */
const garageRef = record({ id: name, name: value.optional() });

const money = record({ currency: name, amountMinor: count });

/** A shop or dealer: online shops by their website's domain (or name), local dealers by `dealerKey`. */
const supplierRow = section({
  key: name,
  name: value,
  garages: count,
  suppliers: count,
  sources: count,
  preferredSources: count,
  orders: count,
  /** Placed orders, without tax, per currency. */
  spend: list(money, 50),
  /** Days since each source's price was set. */
  priceAgeDays: distributionSchema,
}).required({ key: true });

/** One anonymous demo outcome (docs/admin-app/figures.md › Demo outcomes): no email, no ids, days only. */
export const demoOutcomeSchema = section({
  /** A random id of the outcome itself, so a resend doesn't count it twice. */
  id: name,
  endedBy: name,
  endedOn: isoDate,
  requestedOn: isoDate,
  startedOn: isoDate.nullable(),
  language: name.nullable(),
  exampleData: z.boolean().nullable(),
  deviceType: name.nullable(),
  linkOpened: z.boolean(),
  minutesToOpen: count.nullable(),
  welcomeDone: z.boolean(),
  /** From start to end. */
  lifetimeMinutes: count.nullable(),
  /** From start to the last use. */
  spanMinutes: count.nullable(),
  activeSeconds: count,
  visits: count,
  cameBack: z.boolean(),
  requests: count,
  changes: count,
  areasRead: list(name, 30),
  areasChanged: list(name, 30),
  firstAreaChanged: name.nullable(),
  resets: count,
  engagement: name,
}).required({ id: true, endedBy: true, endedOn: true });
export type DemoOutcome = z.infer<typeof demoOutcomeSchema>;

const reportMeta = record({
  reportVersion: count,
  appVersion: z.string().min(1).max(100),
  generatedAt: instant,
  publicUrl: z.string().max(500).nullish(),
  /** The public demo leaves out the garage table and the inventory, supplier and chassis-code sections. */
  publicDemo: z.boolean().optional(),
});

/** The installation report: aggregates only, computed by the installation from its own database. */
export const installationReportSchema = section({
  garages: section({
    total: count,
    /** Left out of statistics: counted here, and kept out of every other figure. */
    statsExcluded: count,
    active,
    addedPerDay: perDay,
    deletedPerDay: perDay,
    /** Of the garages older than 30 days, those active in the last 30. */
    retained: section({ base: count, active: count }),
    /** Garages by the month they were added; `active[i]` is how many were active `i` months later. */
    cohorts: list(record({ month, added: count, active: list(count, 600) }), 600),
    /** Of the garages added in a month, those that reached each step within 30 days. */
    funnel: list(
      record({
        month,
        added: count,
        steps: list(
          section({ step: name, reached: count, medianDays: amount }).required({ step: true }),
          20,
        ),
      }),
      600,
    ),
    lifecycle: section({ unclaimed: count, neverOpened: count, stalled: count, dormant: count }),
    recency,
  }),
  activity: section({
    activeDays30: distributionSchema,
    requestsPerDay: distributionSchema,
    writesPerDay: distributionSchema,
    mostUsed: list(
      section({ garage: garageRef, activeDays30: count, requests30: count }).required({
        garage: true,
      }),
      10,
    ),
  }),
  complexSetups: section({
    /** How many of the ten `FEATURES` each garage uses. */
    breadth: distributionSchema,
    /** Garages per breadth, 0 to 10. */
    breadthCounts: list(count, 16),
    depth: list(
      section({
        measure: name,
        perGarage: distributionSchema,
        top: list(record({ garage: name, value: amount }), 10),
      }).required({ measure: true }),
      50,
    ),
  }),
  users: section({
    total: count,
    byStatus: tally(20),
    admins: count,
    welcomed: count,
    /** Active users who share usage statistics: the coverage of every interaction figure. */
    sharingUsage: count,
    languages: tally(20),
    themes: tally(10),
    active,
    addedPerDay: perDay,
    deletedPerDay: perDay,
    signInsPerDay: perDay,
    activeOnlyOnJoinDay: count,
    /** Not seen for `QUIET_DAYS`: the installation's "gone quiet" filter. */
    goneQuiet: count,
    recency,
    disabled: count,
    membersDeactivated: count,
    perGarageByRole: list(record({ role: name, perGarage: distributionSchema }), 20),
    garagesPerUser: distributionSchema,
    invitations: section({
      open: count,
      openAfter7Days: count,
      expiredUnaccepted: count,
      revoked: count,
      daysToAccept: distributionSchema,
    }),
  }),
  parts: section({
    ...RECORD_FIGURES,
    current: count,
    archived: count,
    byOrigin: tally(10),
    withSource: count,
    withoutSource: count,
    withMinimumStock: count,
    /** Companies, so every one is listed, normalized. */
    manufacturers: tally(5000),
    untouched12Months: count,
    catalogueCoverage: section({ parts: count, inCatalogue: count }),
  }),
  vehicles: section({
    ...RECORD_FIGURES,
    current: count,
    archived: count,
    withChassisCode: count,
    withRoadworthinessDue: count,
    kinds: tally(10),
    makes: tally(1000),
    models: tally(2000),
    engineCodes: tally(2000),
    /** Per bucket of the vehicle age edges (`customers.ts`). */
    ageBuckets: list(count, 32),
    ageUnknown: count,
    /** Per bucket of the mileage edges, in km. */
    mileageBuckets: list(count, 32),
    mileageUnknown: count,
  }),
  segments: section({ garages: tally(20) }),
  spend: section({
    /** Placed orders per month, without tax, by currency; no exchange rates. */
    perMonth: list(record({ month, currency: name, amountMinor: count, orders: count }), 5000),
    /** All placed orders by shop domain and currency. */
    byShop: list(record({ key: name, currency: name, amountMinor: count, orders: count }), 5000),
    perActiveGarage: list(record({ currency: name, perGarage: distributionSchema }), 50),
  }),
  workOrders: section({
    ...RECORD_FIGURES,
    byStatus: tally(10),
    /** Planned and in progress, per garage. */
    openPerGarage: distributionSchema,
    byType: tally(20),
    completedPerDay: perDay,
    /** Imported work orders completed before they were added are skipped. */
    daysToCompletion: distributionSchema,
    fromTemplate: count,
    ideasOlderThan90Days: count,
    plannedPastDate: count,
  }),
  templates: section({
    ...RECORD_FIGURES,
    withServiceInterval: count,
    workOrdersCreatedPerDay: perDay,
  }),
  orders: section({
    ...RECORD_FIGURES,
    byStatus: tally(10),
    placedPerDay: perDay,
    receivedPerDay: perDay,
    daysToReceive: distributionSchema,
    linesPerOrder: distributionSchema,
    draftsOlderThan30Days: count,
  }),
  otherRecords: section({
    locations: section(RECORD_FIGURES),
    tags: section(RECORD_FIGURES),
    attachments: section(RECORD_FIGURES),
    sources: section(RECORD_FIGURES),
  }),
  featureAdoption: section({
    /** The garages the shares are over. */
    garages: count,
    /** Garages using each of the `FEATURES`. */
    features: tally(20),
  }),
  chassisCodes: section({
    /** Per normalized key; a garage's own codes only with 3 or more garages. */
    codes: list(
      section({
        key: name,
        /** The built-in list it comes from, or null for a garage's own. */
        list: name.nullable(),
        vehicles: count,
        parts: count,
        garages: count,
      }).required({ key: true }),
      5000,
    ),
    makePackages: tally(20),
  }),
  suppliers: section({
    ...RECORD_FIGURES,
    byKind: tally(10),
    /** Every online shop, also with one garage: businesses with a public website. */
    shops: list(supplierRow, 5000),
    /** Local dealers used by 3 or more garages only. */
    localDealers: list(supplierRow, 5000),
    preferredSource: section({ parts: count, withPreferred: count }),
    pricesOlderThan12Months: count,
  }),
  garageSettings: section({
    currencies: tally(50),
    mileageUnits: tally(10),
    priceBases: tally(10),
    languages: tally(20),
    roadworthinessDueModes: tally(10),
    baseTaxRates: tally(50),
  }),
  workDone: section({
    workOrderTypes: tally(20),
    mainGroups: tally(500),
    /** With 3 or more garages. */
    tags: tally(2000),
  }),
  friction: section({ welcomeNotFinished: count, demosOpenedNotStarted: count }),
  usage: section({
    /** Whether this carries every kept day (core asked for them) or the last `REPORTED_DAYS`. */
    full: z.boolean(),
    /** Day counts as `[metric, value, count]` (`USAGE_METRICS`), grouped by UTC day. */
    days: list(
      record({ day: isoDate, counts: list(z.tuple([name, value, count]), 5000) }),
      MAX_DAYS,
    ),
  }),
  signInSessions: section({
    live: count,
    perUser: distributionSchema,
    ageBuckets: list(count, 16),
    signOutsPerDay: perDay,
  }),
  demo: section({
    live: count,
    max: count,
    /** Of the last `REPORTED_DAYS`, or of every kept day when core asked for them. */
    outcomes: list(demoOutcomeSchema, 50_000),
  }),
  catalogue: section({ parts: count, lastImportAt: instant.nullable() }),
  storage: section({
    databaseBytes: count,
    largestTables: list(record({ name, bytes: count, rows: count.optional() }), 10),
    connections: count.nullable(),
    fileBytes: section({ total: count, deduped: count, perGarage: distributionSchema }),
    dataFolderBytes: count.nullable(),
    disk: record({ totalBytes: count, freeBytes: count }).nullable(),
  }),
  /** Not with the public demo. */
  garageTable: list(
    section({
      id: name,
      name: value,
      createdOn: isoDate,
      members: count,
      parts: count,
      vehicles: count,
      workOrdersOpen: count,
      workOrdersTotal: count,
      templates: count,
      orders: count,
      fileBytes: count,
      breadth: count,
      activeDays30: count,
      requests30: count,
      lastActiveOn: isoDate.nullable(),
      /** `GARAGE_FLAGS`, and `outlier:<measure>`. */
      flags: list(name, 30),
    }).required({ id: true }),
    5000,
  ),
}).extend({ meta: reportMeta });
export type InstallationReport = z.infer<typeof installationReportSchema>;

/** Latency counts per bucket of `LATENCY_EDGES_MS`. */
const latency = list(count, 32);

/**
 * The installation status: counters kept in memory since `startedAt`, so they only grow until a
 * restart. Core turns the differences between two statuses into daily figures, and a changed
 * `startedAt` tells it the counters started again from zero.
 */
export const installationStatusSchema = record({
  reportVersion: count,
  appVersion: z.string().min(1).max(100),
  startedAt: instant,
  /** When the counters were read. */
  at: instant,
  publicDemo: z.boolean().optional(),
  requests: list(record({ group: name, statusClass: name, count, latency }), 500).optional(),
  procedures: list(
    section({
      path: name,
      calls: count,
      unexpected: count,
      latency,
      /** The `AppError` codes users ran into: friction, not failures. */
      appErrors: list(record({ code: name, count }), 200),
    }).required({ path: true }),
    2000,
  ).optional(),
  /** The last unexpected errors: never a message. */
  recentErrors: list(
    record({ at: instant, where: name, errorClass: name, sqlState: name.nullable() }),
    20,
  ).optional(),
  /** `SIGNALS`, each with its value. */
  signals: list(record({ signal: name, value, count }), 10_000).optional(),
  process: section({
    /** Since the previous status. */
    eventLoopDelayMs: section({ p50: amount, p99: amount, max: amount }),
    rssBytes: count,
    heapUsedBytes: count,
    heapTotalBytes: count,
    /** The container's memory (cgroup v2), or null outside one. */
    memoryCurrentBytes: count.nullable(),
    memoryLimitBytes: count.nullable(),
    oomKills: count.nullable(),
    uptimeSeconds: amount,
    database: record({ up: z.boolean(), latencyMs: amount.nullable() }),
  }).optional(),
});
export type InstallationStatus = z.infer<typeof installationStatusSchema>;

/** Core's answer to a status or report. */
export const ingestResponseSchema = record({
  ok: z.boolean(),
  /** The usage history has a gap: the next report carries all `KEPT_DAYS`. */
  resendUsage: z.boolean().optional(),
});
export type IngestResponse = z.infer<typeof ingestResponseSchema>;
